import { useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import NoIndex from "../components/NoIndex";

export default function RequestConfirmation() {
  const [form, setForm] = useState({
    company_name: "", business_type: "car_rental", city: "",
    company_email: "", company_phone: "", company_facebook: "",
    customer_name: "", customer_email: "", customer_phone: "",
    booking_start: "", booking_end: "", vehicle_or_service: "",
    pickup_location: "", price: "", advance: "", deposit: "", service_mode: "", message: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        price: form.price ? Number(form.price) : null,
        deposit: form.deposit ? Number(form.deposit) : null,
        advance: form.advance ? Number(form.advance) : null,
        service_mode: form.service_mode || null,
        booking_start: form.booking_start ? new Date(form.booking_start).toISOString() : null,
        booking_end: form.booking_end ? new Date(form.booking_end).toISOString() : null,
      };
      const { data } = await api.post("/public/verification-request", payload);
      setResult(data);
      toast.success("Confirmation request submitted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-8 text-center" data-testid="rc-success">
          <CheckCircle2 className="w-10 h-10 text-emerald-700 mx-auto" />
          <h2 className="mt-3 font-display text-2xl font-semibold text-emerald-900">Request sent</h2>
          <p className="mt-2 text-emerald-900">
            We've notified the company. If they're on ConfirmPH, they'll review and respond.
            {result.company_status === "unclaimed" && " If they're not registered yet, we've created an unclaimed profile and saved their record. They may receive an invite to claim it."}
          </p>
          <p className="mt-3 text-xs text-emerald-800 font-mono">Request ID: {result.request.id}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-8" data-testid="request-confirmation-page">
      <NoIndex title="Request Booking Confirmation · ConfirmPH" />
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold">Request written booking confirmation</h1>
        <p className="mt-2 text-slate-600">Booked via Messenger, phone, or walk-in and want it confirmed in writing? Submit a request and we'll ask the business to record and confirm the booking details.</p>
      </div>

      <form onSubmit={submit} className="rounded-xl border border-slate-200 bg-white p-6 space-y-6">
        <section>
          <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">About the business</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Company name" required testId="rc-company-name" value={form.company_name} onChange={set("company_name")} className="sm:col-span-2" />
            <F label="City / area" testId="rc-city" value={form.city} onChange={set("city")} />
            <F label="Phone (if known)" testId="rc-company-phone" value={form.company_phone} onChange={set("company_phone")} />
            <F label="Email (if known)" type="email" testId="rc-company-email" value={form.company_email} onChange={set("company_email")} />
            <F label="Facebook page (if known)" testId="rc-company-fb" value={form.company_facebook} onChange={set("company_facebook")} />
          </div>
        </section>

        <section>
          <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">About you</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Your name" required testId="rc-cust-name" value={form.customer_name} onChange={set("customer_name")} />
            <F label="Your email" type="email" required testId="rc-cust-email" value={form.customer_email} onChange={set("customer_email")} />
            <F label="Your phone" testId="rc-cust-phone" value={form.customer_phone} onChange={set("customer_phone")} />
          </div>
        </section>

        <section>
          <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-3">Booking details</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Vehicle / item / service" testId="rc-vehicle" value={form.vehicle_or_service} onChange={set("vehicle_or_service")} className="sm:col-span-2" />
            <F label="Pickup / start location" testId="rc-pickup" value={form.pickup_location} onChange={set("pickup_location")} />
            <F label="Price (PHP, if known)" type="number" testId="rc-price" value={form.price} onChange={set("price")} />
            <F label="Booking start" type="datetime-local" testId="rc-start" value={form.booking_start} onChange={set("booking_start")} />
            <F label="Booking end" type="datetime-local" testId="rc-end" value={form.booking_end} onChange={set("booking_end")} />
            <F label="Advance / downpayment (PHP, if any)" type="number" testId="rc-advance" value={form.advance} onChange={set("advance")} />
            <F label="Refundable security deposit (PHP, if any)" type="number" testId="rc-deposit" value={form.deposit} onChange={set("deposit")} />
            <label>
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Service</span>
              <select data-testid="rc-service-mode" value={form.service_mode} onChange={set("service_mode")}
                      className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none bg-white">
                <option value="">Not specified</option>
                <option value="self_drive">Self-drive</option>
                <option value="with_driver">With driver</option>
                <option value="unsure">Unsure</option>
              </select>
            </label>
          </div>
          <label className="block mt-4">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Message / context</span>
            <textarea data-testid="rc-message" value={form.message} onChange={set("message")} rows={3} className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
          </label>
        </section>

        <button type="submit" disabled={loading} className="w-full sm:w-auto inline-flex items-center px-5 py-3 rounded-lg btn-brand font-medium disabled:opacity-60" data-testid="rc-submit">
          {loading ? "Submitting…" : "Submit confirmation request"}
        </button>
        <p className="text-xs text-slate-500" data-testid="rc-privacy-note">
          Your details are shared with the business so it can handle this request, and are processed
          as described in the <Link to="/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </form>
    </div>
  );
}

function F({ label, value, onChange, type = "text", required, testId, className = "" }) {
  return (
    <label className={className}>
      <span className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</span>
      <input data-testid={testId} type={type} required={required} value={value} onChange={onChange}
        className="mt-1.5 w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:border-[#2D6A4F] outline-none" />
    </label>
  );
}
